import React, { useEffect } from "react";
import { Badge } from "antd";
import { BellOutlined, SettingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import WalletHeaderButton from "../components/WalletHeaderButton";
import MultiNetworkDepositDrawer from "../components/MultiNetworkDepositDrawer";
import WorkingBalanceWithdrawDrawer from "../components/WorkingBalanceWithdrawDrawer";
import { useMultiWallet } from "../hooks/useMultiWallet";
import { RootState } from "../services/store";
import KredafiLogo from "./KredafiLogo";
import {
  KredafiCabinetProvider,
  KredafiBorrowPanel,
  KredafiIssuePanel,
  useKredafiCabinet,
} from "./KredafiCabinet";
import "./kredafi.css";

interface Props {
  children: React.ReactNode;
}

type TopTab = "home" | "deposit" | "issue" | "borrow" | "withdraw";
type BottomTab = "loans" | "history" | "bonus" | "profile" | "partners";

function topTabFromLocation(path: string, panel: string): TopTab {
  if (panel === "deposit") return "deposit";
  if (panel === "issue") return "issue";
  if (panel === "borrow") return "borrow";
  if (panel === "withdraw") return "withdraw";
  if (path.startsWith("/loan-insure")) return "borrow";
  if (path.startsWith("/loan-overnight")) return "issue";
  return "home";
}

function bottomTabFromLocation(
  path: string,
  userId: number | undefined,
  panel: string,
  state?: any
): BottomTab | null {
  if (panel !== "none") return null;
  if (path.startsWith("/loan-issuance") && state?.useBonus) return "bonus";
  if (path.startsWith("/loans") || path.startsWith("/loan/")) return "loans";
  if (path.startsWith("/history")) return "history";
  if (path.startsWith("/chat/") || path.startsWith("/post/") || path === "/feed") {
    return "profile";
  }
  if (path.startsWith("/profile")) return "profile";
  if (path.startsWith("/invest-partners-balance")) return "partners";
  if (path.startsWith("/partners")) {
    const own = path.match(/^\/partners\/(\d+)/);
    if (own && userId && Number(own[1]) === userId) return "profile";
    return "partners";
  }
  return null;
}

const KredafiPageInner: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useSelector((state: RootState) => state.user);
  const multiWallet = useMultiWallet();
  const cabinet = useKredafiCabinet();
  const top = topTabFromLocation(location.pathname, cabinet.panel);
  const bottom = bottomTabFromLocation(
    location.pathname,
    userData?.id,
    cabinet.panel,
    location.state
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, cabinet.panel]);

  const goHome = () => {
    cabinet.closePanel();
    if (location.pathname !== "/") navigate("/");
  };

  const go = (path: string) => {
    cabinet.closePanel();
    if (location.pathname !== path || location.search) {
      navigate(path);
    }
  };

  const topTabs: { id: TopTab; label: string; onClick: () => void }[] = [
    {
      id: "home",
      label: t("Home") as string,
      onClick: goHome,
    },
    {
      id: "deposit",
      label: t("Top up") as string,
      onClick: () => cabinet.openDeposit(),
    },
    {
      id: "issue",
      label: t("Place nav") as string,
      onClick: () => cabinet.openIssue(),
    },
    {
      id: "borrow",
      label: t("Borrow") as string,
      onClick: () => cabinet.openBorrow(),
    },
    {
      id: "withdraw",
      label: t("Withdraw") as string,
      onClick: () => cabinet.openWithdraw(),
    },
  ];

  const bottomTabs: { id: BottomTab; label: string; onClick: () => void }[] = [
    {
      id: "loans",
      label: t("My Loans") as string,
      onClick: () => go("/loans"),
    },
    {
      id: "history",
      label: t("history") as string,
      onClick: () => go("/history"),
    },
    {
      id: "bonus",
      label: t("Bonuses") as string,
      onClick: () => {
        cabinet.closePanel();
        navigate("/loan-issuance", { state: { useBonus: true } });
      },
    },
    {
      id: "partners",
      label: t("Partners") as string,
      onClick: () => go("/partners"),
    },
    {
      id: "profile",
      label: t("profile") as string,
      onClick: () => go("/profile"),
    },
  ];

  const showPanel = cabinet.panel !== "none";

  return (
    <div className="kf-app">
      <nav className="kf-navpill" aria-label={t("Home") as string}>
        {topTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={top === tab.id ? "on" : ""}
            onClick={tab.onClick}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="kf-shell">
        <div className="kf-shell-head">
          <span
            onClick={goHome}
            role="link"
            tabIndex={0}
            className="kf-shell-logo"
            onKeyDown={(e) => {
              if (e.key === "Enter") goHome();
            }}
          >
            <KredafiLogo size={48} opticalAlign />
          </span>
          <div className="kf-shell-actions">
            <WalletHeaderButton />
            <Badge
              count={userData?.unread_notifications || 0}
              size="small"
              color="#0aa8a5"
            >
              <button
                type="button"
                className="kf-iconbtn kf-iconbtn-bell"
                onClick={() => {
                  cabinet.closePanel();
                  navigate("/notifications");
                }}
                aria-label={t("Notifications") as string}
              >
                <BellOutlined />
              </button>
            </Badge>
            <button
              type="button"
              className="kf-iconbtn"
              onClick={() => {
                cabinet.closePanel();
                navigate("/settings");
              }}
              aria-label={t("settings") as string}
            >
              <SettingOutlined />
            </button>
          </div>
        </div>

        <div className="kf-body">
          {cabinet.panel === "deposit" ? (
            <MultiNetworkDepositDrawer
              open
              inline
              voucherMode={cabinet.voucherMode}
              onClose={cabinet.closePanel}
              activeNetwork={multiWallet.activeNetwork}
              onDepositSuccess={() => {
                multiWallet.refreshBalances();
                cabinet.reloadAssets();
                [10000, 30000, 60000].forEach((d) =>
                  setTimeout(cabinet.reloadAssets, d)
                );
              }}
            />
          ) : null}
          {cabinet.panel === "issue" ? (
            <KredafiIssuePanel onClose={cabinet.closePanel} />
          ) : null}
          {cabinet.panel === "borrow" ? (
            <KredafiBorrowPanel onClose={cabinet.closePanel} />
          ) : null}
          {cabinet.panel === "withdraw" ? (
            <WorkingBalanceWithdrawDrawer
              open
              inline
              onClose={cabinet.closePanel}
              activeNetwork={multiWallet.activeNetwork}
              assets={cabinet.assets}
              onSuccess={() => cabinet.reloadAssets()}
            />
          ) : null}
          <div className={showPanel ? "kf-body-hidden" : undefined}>{children}</div>
        </div>
      </div>

      <nav className="kf-bottomnav" aria-label={t("My Loans") as string}>
        {bottomTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={bottom === tab.id ? "on" : ""}
            onClick={tab.onClick}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

const KredafiPage: React.FC<Props> = ({ children }) => (
  <KredafiCabinetProvider>
    <KredafiPageInner>{children}</KredafiPageInner>
  </KredafiCabinetProvider>
);

export default KredafiPage;
